<template>
  <div class="events">
    <data-table
      :show-filter="true"
      v-if="eventsData"
      header-text="Events log"
      header-icon="nav-layers-line-apioshade3"
      :headers="fields"
      :items="eventsData"
      :count="eventsDataCount"
      :showSelectedRow="true"
      perPageDesc="rows"
      item-key="ref"
      :loaded="loaded"
      @refresh-data="updateData"
    >
      <template slot="activator">
        <apio-switch
          v-model="liveMode"
          v-on:change="toggleLiveMode"
          :color="'green'"
          :key="liveModeKey"
          ><template slot="left-label"
            ><span class="small-extra-bold">Live mode</span></template
          ></apio-switch
        >
      </template>

      <table-filter
        slot="table-filter"
        @toggleFilter="toggleFilter"
        :show-filter="true"
        :filtered="filtered"
        class="my-1"
      />
     <template v-slot:[`item.status`]="{ item }">
        <events-status-tile style="display: flex" :status="item.status" />
      </template>

      <template v-slot:[`item.ref`]="{ item }">
      <div class="refentry" :title="item.ref">
        <span>{{ item.ref }}</span>
        <div title="Copy ID">
          <apio-icon
            @click.native="copyToClipboard(item.ref)"
            size="14"
            name="ui-copy"
            class="ml-1"
            style="cursor: pointer"
          ></apio-icon>
        </div>
      </div>
      </template>

      <template v-slot:[`item.eventdate`]="{ item }">
        <div>{{ item.eventdate | dateTimeFormat }}</div>
      </template>

      <template v-slot:[`item.size`]="{ item }">
        <div>{{ item.size }}</div>
      </template>

      <template v-slot:[`item.message`]="{ item }">
        <div class="messageentry">{{ item.message }}</div>
      </template>

      <template v-slot:[`item.interface`]="{ item }">
        <div class="xintf" v-if="userBuildRights(item.clientHash)">
          <a @click="goToOrg(item)">{{ item.clientName }}</a
          ><br /><a @click="goToApp(item)">{{ item.appName }}</a>
          <div>{{ item.interface }}</div>
        </div>
        <div class="xintf" v-else>
          <div>{{ item.clientName }}</div>
          <div>{{ item.appName }}</div>
          <div>{{ item.interface }}</div>
        </div>
      </template>

      <template v-slot:[`item.desc`]="{ item }">
        <div class="d-flex align-center">
          <div class="multiline-truncate">{{ item.desc }}</div>
          <apio-icon
            @click.native="copyToClipboard(item.desc)"
            size="14"
            name="ui-copy"
            class="ml-1"
            style="cursor: pointer; min-width: 14px"
          ></apio-icon>
        </div>
      </template>

      <template v-slot:[`item.step`]="{ item }">
        <div>{{ item.step }}</div>
      </template>

      <template
        v-slot:[`header.show_details`]="{}"
        v-if="!$vuetify.breakpoint.xs && !$vuetify.breakpoint.sm"
      >
        <!-- Live Mode Button -->
        <table-filter
          @toggleFilter="toggleFilter"
          :show-filter="true"
          :filtered="filtered"
          class="my-1"
        />
      </template>
      <template v-slot:[`item.show_details`]="{ item }">
        <div
          v-if="item.hasdata"
          class="events__expand-icon"
          :ripple="false"
          @click="toggleEventDetails(item)"
        >
          <apio-icon
            name="chevron-right"
            lightModeColour="black"
            darkModeColour="white"
          ></apio-icon>
        </div>
      </template>
    </data-table>

    <events-filter
      @filterUpdate="filterUpdate"
      @updateDateRange="updateDateRange"
      @toggleFilter="toggleFilter"
      :dialog="showFilter"
      :selectedApp="selectedApp"
      :selectedInterface="selectedInterface"
      :selectedStatuses="selectedStatuses"
      :selectedGroup="selectedGroup"
      :selectedFrom="selectedFrom"
      :selectedTo="selectedTo"
    />
    <apio-snackbar
      :text="snackBarText"
      :status="snackBarStatus"
      :snackbar="snackbar"
      @close="snackbar = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { dashboard } from "@/mixins/dashboard.js";
import dayjs from "dayjs";
var utc = require("dayjs/plugin/utc"); // dependent on utc plugin
var timezone = require("dayjs/plugin/timezone");
dayjs.extend(utc);
dayjs.extend(timezone);
import * as clipboard from "clipboard-polyfill/text";

export default {
  name: "Events",
  components: {
    ApioSwitch: () => import("@/components/Misc/Switch.vue"),
    EventsFilter: () => import("@/pages/Events/EventsFilter.vue"),
    TableFilter: () => import("@/components/DataTable/TableFilter.vue"),
    EventsStatusTile: () => import("./EventsStatusTile"),
    DataTable: () => import("@/components/DataTable/DataTable.vue"),
  },
  mixins: [dashboard()],
  data() {
    return {
      loaded: false,
      modalOpen: false,
      c: "*",
      i: null,
      id: null,
      liveMode: false,
      liveModeKey: Math.random(),
      key: Math.random(),
      filtered: false,
      eventLogDetailView: false,
      selectedApp: null,
      selectedGroup: null,
      selectedInterface: null,
      selectedFrom: null,
      selectedFromUnix: null,
      selectedToUnix: null,
      selectedTo: null,
      selectedStatuses: {},
      showFilter: false,
      l: this.$route.query.l || 10,
      s: this.$route.query.s || 0,
      pagingOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
      },
      initialPageRetrieved: false,
      previousPage: 0,
      search: "",
      expanded: [],
      headers: [
        {
          text: "Status",
          class: "status",
          width: 72,
          fixed: true,
        },
        { text: "ID", width: 230 },
        { text: "Date / time" },
        { text: "Interface" },
        { text: "Steps" },
        { text: "Size" },
        { text: "Message" },
        { text: "", width: 0 },
      ],
      fields: [
        {
          value: "status",
          text: "Status",
        },
        {
          value: "ref",
          text: "ID",
        },
        {
          value: "eventdate",
          text: "Date/Time",
        },
        {
          value: "interface",
          text: "Interface",
        },
        {
          value: "step",
          text: "Steps",
        },
        {
          value: "size",
          text: "Size",
        },
        {
          value: "desc",
          text: "Message",
        },
        {
          value: "show_details",
          text: "",
        },
      ],
      snackbar: false,
      snackBarText: "",
      snackBarStatus: null,
    };
  },
  beforeRouteLeave (to, from, next) { // this only fires on actual navigation away, not reload
    this.$store.dispatch("setEventsLogFilterOptions", {});
    next();
  },
  computed: {
    ...mapState({
      eventsLogFilterOptions: (state) => state.events.eventsLogFilterOptions || {},
      eventsData: (state) => state.events.eventsData,
      eventsDataCount: (state) => state.events.eventsDataCount,
      eventLogSelectedEvent: (state) => state.events.eventLogSelectedEvent,
    }),
    ...mapGetters(["userBuildRights"]),
    events() {
      return [];
    },
    startDate() {
      return dayjs.tz(this.timestampStart * 1000).utc();
    },
    endDate() {
      return dayjs.tz(this.timestampEnd * 1000).utc();
    },
  },
  filters: {
    dateTimeFormat(dateTime) {
      // incoming format 2021-03-01T10:44:34Z
      const date = new dayjs.tz(dateTime).format("DD/MM/YYYY @ HH:mm:ss");
      return date;
    },
  },
  mounted () {
    window.addEventListener('keydown', this.keyDownHandler);
    this.$root.$on('set-modal-open', this.setModalOpen);
  },
  unmounted () {
    window.removeEventListener('keydown', this.keyDownHandler);
  },
  methods: {
    setModalOpen(val) {
      this.modalOpen = val;
    },
    keyDownHandler (event) {
      if (!this.modalOpen && event.code === 'Escape') {
        this.$store.commit("set", ["eventLogDetailViewState", false]);
      }
    },
    resetPagination() {
      this.pagingOptions = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
      }
    },
    updateDateRange(val) {
      this.filterDateRange(val);
    },
    goToOrg(item) {
      // check for build perms
      this.$router.push(`/organisation-dashboard?c=${item.clientHash}`);
    },
    goToApp(item) {
      // check for build perms
      this.$router.push(`/build?c=${item.clientHash}&a=${item.appHash}`);
    },
    selfRefresh() {
      if (this.liveMode) {
        this.updateData(this.pagingOptions);
        setTimeout(() => {
          this.selfRefresh();
        }, 5000);
      }
    },
    toggleLiveMode() {
      this.liveMode = !this.liveMode;
      this.liveModeKey = Math.random();
      if (this.liveMode) {
        this.selfRefresh();
      }
      // const appHash = this.$route.query.a || this.selectedApp;
      // const interfaceHash = this.$route.query.i || this.selectedInterface;
      // const client = this.$route.query.c || this.c;
      // const id = this.eventsData[0].ref;
      // if (this.liveMode) {
      //   try {
      //     this.$store.dispatch("startDataStream", {
      //       interfaceHash,
      //       appHash,
      //       client,
      //       id,
      //     });
      //   } catch (e) {
      //     console.log(e);
      //     this.$store.commit("TOGGLE_LIVE_MODE");
      //   }
      // } else {
      //   this.$store.dispatch("stopDataStream", {
      //     interfaceHash,
      //     appHash,
      //     client,
      //   });
      // }
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    filterDateRange(val) {
      val?.startDate.setHours(0, 0, 0, 0);
      val?.endDate.setHours(23, 59, 59, 59);
      this.selectedFromUnix = val.selectedFromUnix;
      this.selectedToUnix = val.selectedToUnix;
      this.updateData(this.pagingOptions);
    },
    async toggleEventDetails(item) {
      this.$store.commit("set", ["eventLogDetailViewState", false]);
      await this.$store.dispatch("setEventLogSelectedEvent", item);
      this.$store.commit("set", ["eventLogDetailViewState", true]);
    },
    getStoredFilterOptions() {
      this.selectedFromUnix = this.eventsLogFilterOptions?.selectedFromUnix;
      this.selectedToUnix = this.eventsLogFilterOptions?.selectedToUnix;

      if (!this.selectedFromUnix) {
        let initialStartDate = new Date();
        initialStartDate.setHours(0, 0, 0, 0);
        this.selectedFromUnix = dayjs.tz(initialStartDate).utc().unix();
      }

      if (!this.selectedToUnix) {
        let initialEndDate = new Date();
        initialEndDate.setHours(23, 59, 59, 59);
        this.selectedToUnix = dayjs.tz(initialEndDate).utc().unix();
      }

      this.selectedFrom = this.eventsLogFilterOptions?.selectedFrom;
      this.selectedTo = this.eventsLogFilterOptions?.selectedTo;
      this.selectedApp = this.eventsLogFilterOptions?.selectedApp;
      this.selectedGroup = this.eventsLogFilterOptions?.selectedGroup;
      this.selectedInterface = this.eventsLogFilterOptions?.selectedInterface;
      this.selectedStatuses = this.eventsLogFilterOptions?.selectedStatuses;
      if (this.eventsLogFilterOptions?.q) this.pagingOptions.q = this.eventsLogFilterOptions?.q;
      if(this.selectedApp || this.selectedGroup || this.selectedInterface) {
        this.filtered = true;
      }
    },
    filterUpdate(filterOptions) {
      this.$store.dispatch("setEventsLogFilterOptions", filterOptions);
      this.selectedFrom = filterOptions.dateRange?.startDate;
      this.selectedTo = filterOptions.dateRange?.endDate;
      this.selectedApp = filterOptions.selectedApp;
      this.selectedGroup = filterOptions.selectedGroup;
      this.selectedInterface = filterOptions.selectedInterface;
      this.selectedStatuses = filterOptions.selectedStatuses;
      if (filterOptions.q) this.pagingOptions.q = filterOptions.q;
      if(this.selectedApp || this.selectedGroup || this.selectedInterface) {
        this.filtered = true;
      }
      this.updateData(this.pagingOptions);
    },
    async copyToClipboard(val) {
      const vm = this;
      await clipboard.writeText(val).then(
        function () {
          vm.snackBarText = "Copied to clipboard";
          vm.snackBarStatus = "success";
          vm.snackbar = true;
        },
        function () {
          vm.snackBarText = "Could not copy value to the clipboard";
          vm.snackBarStatus = "error";
          vm.snackbar = true;
        }
      );
    },
    async updateData(pagingOptions) {
//      if(this.liveMode) { this.toggleLiveMode(); }
      await this.$store.dispatch("fetchEventsData", {
        startDate: this.$route.query.f || this.eventsLogFilterOptions?.selectedFromUnix,
        endDate: this.$route.query.t || this.eventsLogFilterOptions?.selectedToUnix,
        client: this.$route.query.c || "*",
        app: this.selectedApp || this.$route.query.a,
        interface: this.selectedInterface || this.$route.query.i,
        o: (pagingOptions.page - 1) * pagingOptions.itemsPerPage || 0,
        l: pagingOptions.itemsPerPage || 10,
        q: pagingOptions.q || "",
        g: this.selectedGroup || this.$route.query.g, //this is a guess..
      });

      this.resetPagination();
    },
  },
  async beforeRouteEnter(to, from, next) {
    await next(async vm => {
      vm.getStoredFilterOptions();
      await vm.updateData(vm.pagingOptions);
      vm.loaded = true;
    });
  }

  // async created() {
  //   this.getStoredFilterOptions();
  //   await this.updateData(this.pagingOptions);
  //   this.loaded = true;
  // },
};
</script>

<style lang="scss" scoped>

::v-deep tr.v-data-table__selected {
    background: #7d92f5 !important;
  }

::v-deep .multiline-truncate {
  width: 200px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

::v-deep .v-data-table__wrapper > table > thead > tr > th:nth-child(1) {
  background: var(--apio-shade-2);
  max-width: 72px;
  width: 72px;

  > span {
    color: white !important;
  }
}

::v-deep .text-start.apioshade2.white--text.p2-extra-bold {
  width: 70px;
}

@media screen and (max-width: 769px) {
  ::v-deep tr.v-data-table__mobile-table-row > td:nth-child(1) {
    padding-right: 0;
  }

  ::v-deep .v-data-table__mobile-table-row:first-of-type() {
    padding-left: 16px;
    height: 50px;
  }

}

.xintf {
  height: 54px;
  overflow: hidden;
}


@media screen and (min-width: 769px) {
  ::v-deep
    .v-data-table__wrapper
    > table:nth-child(1)
    > tbody
    > tr
    > td:nth-child(1) {
    padding: 0;
  }
}

.refentry {
  display: flex;
  align-items: center;
}

.refentry span {
  width: 20px;
  overflow: hidden;
}

@media screen and (min-width: 1200px) {
  .refentry span {
    width: auto;
  }
  .multiline-truncate {
    width: 40px;
  }
}

.events {
  &__expand-icon {
    display: flex;
    justify-content: flex-end;
    cursor: pointer;
  }

  &__log-detail-view {
    width: 600px;
  }

  &__add-button {
    margin: 0 12px;
    width: 32px;
    height: 32px;
    display: flex;

    &--mobile {
      background: var(--apio-cyan);
      background-size: cover;
      border-radius: 50%;
      margin: 0 12px;
      height: 32px;
      width: 32px;
      position: relative;

      & > .icon-24.grunticon__icon-ui-plus-white {
        position: relative;
        top: 4px;
        left: 4px;
      }
    }

    &--mobile:hover {
      cursor: pointer;
    }
  }
}

@media (min-width: 769px) {
  .events {
    &__add-button {
      margin: 0 12px;
      width: fit-content;
      height: 48px;
    }
  }
}
</style>
